import { computed, inject, Injectable, signal } from "@angular/core";
import { firstValueFrom } from "rxjs";
import { HttpClient } from "@angular/common/http";
import LogoutResult = stages.authenticationservice.core.LogoutResult;

export type AuthStatus = "anonymous" | "authenticated" | "unknown";

@Injectable({ providedIn: "root" })
export class AuthService {
	private http = inject(HttpClient);

	private _status = signal<AuthStatus>("unknown");
	private _me = signal<stages.authenticationservice.main.AuthenticationResourceMeResponse | null>(null);

	readonly status = this._status.asReadonly();
	readonly me = this._me.asReadonly();
	readonly isAuthenticated = computed(() => this._status() === "authenticated");

	async refresh(): Promise<void> {
		const me = await firstValueFrom(
			this.http.get<stages.authenticationservice.main.AuthenticationResourceMeResponse>("auth/public/me"),
		); // backend returns 200 always
		this._me.set(me);
		this._status.set(me.authenticated ? "authenticated" : "anonymous");
	}

	async passwordAuthentication(
		credentials: stages.authenticationservice.core.AuthenticationCredentials,
	): Promise<void> {
		await firstValueFrom(
			this.http.post<stages.authenticationservice.core.AuthenticationCredentials>("auth/public/password", credentials, {
				observe: "response",
			}),
		);
		await this.refresh();
	}

	async logout(): Promise<LogoutResult> {
		const logoutResult = await firstValueFrom(this.http.delete<LogoutResult>("auth/public/logout"));
		await this.refresh();
		return logoutResult;
	}

	async startSudo(credentials: stages.authenticationservice.core.AuthenticationCredentials): Promise<void> {
		return firstValueFrom(this.http.post<void>("auth/protected/startsudo", credentials));
	}

	async quitSudo(): Promise<void> {
		return firstValueFrom(this.http.delete<void>("auth/protected/quitsudo"));
	}
}
